import { useEffect, useState, useRef } from "react";
import { Bell } from "lucide-react";
import api from "@/lib/api";
import { formatDate } from "@/lib/dateFmt";
import { useNavigate } from "react-router-dom";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef();
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch {}
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 20000);
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => { clearInterval(iv); document.removeEventListener("mousedown", onClick); };
  }, []);

  const unread = items.filter((n) => !n.read).length;

  const openItem = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.id}/mark-read`).catch(() => {});
      setItems((s) => s.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    setOpen(false);
    if (n.ref_code) navigate(`/requisitions/${n.ref_code}`);
  };

  const markAll = async () => {
    await api.post("/notifications/mark-all-read").catch(() => {});
    setItems((s) => s.map((x) => ({ ...x, read: true })));
  };

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-full hover:bg-[#f4e9eb] transition-colors"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell size={20} className="text-[#5a1520]" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 bg-[#b3261e] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
            data-testid="notification-unread-count"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-96 max-h-[520px] overflow-hidden bg-white border border-[#e2d7c3] rounded-lg shadow-xl z-50"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#e2d7c3] bg-[#f9f4e8]">
            <span className="font-serif font-semibold text-[#7a1f2b]">Notifications</span>
            <button className="text-xs link-vgws" onClick={markAll} data-testid="mark-all-read-button">
              Mark all read
            </button>
          </div>
          <div className="overflow-y-auto max-h-[440px] divide-y divide-[#f2ecdf]">
            {items.length === 0 && (
              <div className="p-6 text-center text-sm text-[#6e7770]">No notifications yet.</div>
            )}
            {items.slice(0, 10).map((n) => (
              <button
                key={n.id}
                onClick={() => openItem(n)}
                className={`w-full text-left px-4 py-3 hover:bg-[#faf5eb] transition-colors ${!n.read ? "bg-[#fdf9ed]" : ""}`}
                data-testid={`notification-item-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 block w-2 h-2 rounded-full bg-[#b3261e] shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#1f2320] truncate">{n.title}</div>
                    <div className="text-xs text-[#4a524c] mt-0.5 line-clamp-2">{n.body}</div>
                    <div className="text-[11px] text-[#6e7770] mt-1">{formatDate(n.created_at, { includeTime: true })}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
          <div className="border-t border-[#e2d7c3] bg-[#faf5eb] px-4 py-2 text-right">
            <button className="text-xs link-vgws" onClick={() => { setOpen(false); navigate("/notifications"); }} data-testid="view-all-notifications">
              View all →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
